import * as React from 'react';
import {Button, view, Link, Icon, SelectField, SelectFieldItem} from '@sail/ui';
import {download, external} from '@sail/icons/react/Icon';
import {
  InlineDatumReactComponentProps,
  useAnalytics,
} from '@stripe-internal/inline-data';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';
import {safeWindowOpen} from '@stripe-internal/safe-links';

type Files = {
  name: string;
  link: string;
};

type ValueProps = {
  files: Files[];
  express_dashboard_link?: string;
  document_page_url: string;
};

const messages = defineMessages({
  downloadInstructionsStripe: {
    id: 'tax1099.previous.download.instructionsStripe',
    description: 'Instructions for downloading 1099 forms for Stripe users',
    defaultMessage:
      'Got it. You can download your prior years 1099-K forms below or via the {documentsLink} page on the Stripe dashboard.',
  },
  downloadInstructionsExpress: {
    id: 'tax1099.previous.download.instructionsExpress',
    description: 'Instructions for downloading 1099 forms for Express users',
    defaultMessage:
      'All of your past tax documents are available from the Express dashboard by year. You will be asked to provide the last four digits of your TIN for security purposes to download them.',
  },
  documentsLinkText: {
    id: 'tax1099.download.documentsLinkText',
    description: 'Text for the link to the Documents page',
    defaultMessage: 'Documents',
  },
  downloadButton: {
    id: 'tax1099.download.downloadButton',
    description: 'Text for Download button',
    defaultMessage: 'Download',
  },
  expressDashboardButton: {
    id: 'tax1099.download.expressDashboardButton',
    description: 'Text for Express Dashboard button',
    defaultMessage: 'Express dashboard',
  },
  chooseOneOption: {
    id: 'tax1099.download.chooseOneOption',
    description: 'Default text for the form select dropdown',
    defaultMessage: 'Choose an option',
  },
  selectLabel: {
    id: 'tax1099.download.selectLabel',
    description: 'Label for the 1099 Documents select dropdown',
    defaultMessage: '1099-K Documents',
  },
});

export const TaxForm1099PreviousYear = (
  props: InlineDatumReactComponentProps,
) => {
  const analytics = useAnalytics();
  const intl = useIntl();
  const {datum} = props;
  const value = datum.value as ValueProps;
  const {files} = value;
  const [selectedOption, setSelectedOption] = React.useState('');

  const isExpressUser = !!value.express_dashboard_link;

  const handleButtonClick = () => {
    if (isExpressUser) {
      safeWindowOpen(value.express_dashboard_link);
      analytics.action('uh_express_dashboard_link_open');
    } else {
      const selectedFile = files.find((file) => file.name === selectedOption);
      if (selectedFile) {
        const link = document.createElement('a');
        link.href = selectedFile.link;
        link.download = `${selectedFile.name}.pdf`;
        link.target = '_blank';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      analytics.action('uh_stripe_tax_form_download');
    }
  };

  return (
    <view.div>
      <view.div css={{marginBottom: 'xxsmall'}}>
        <FormattedMessage
          {...(isExpressUser
            ? messages.downloadInstructionsExpress
            : messages.downloadInstructionsStripe)}
          values={{
            documentsLink: (
              <Link
                type="primary"
                href={value.document_page_url}
                target="_blank"
                rel="noreferrer"
              >
                <FormattedMessage {...messages.documentsLinkText} />
              </Link>
            ),
          }}
        />
      </view.div>
      {!isExpressUser ? (
        <view.div style={{marginBottom: 'medium'}}>
          <SelectField
            css={{minWidth: 'fill'}}
            label={intl.formatMessage(messages.selectLabel)}
            placeholder={intl.formatMessage(messages.chooseOneOption)}
            selectedKey={selectedOption}
            onSelectionChange={(newFile) => {
              setSelectedOption(newFile as string);
            }}
          >
            {files.map((file) => (
              <SelectFieldItem key={`${file.name}-${file.link}`} id={file.name}>
                {file.name}
              </SelectFieldItem>
            ))}
          </SelectField>
        </view.div>
      ) : null}
      <Button
        type="primary"
        css={{width: 'fill', marginBottom: 'small', marginTop: 'small'}}
        onClick={handleButtonClick}
        disabled={!isExpressUser && !selectedOption}
      >
        {isExpressUser ? (
          <>
            <FormattedMessage {...messages.expressDashboardButton} />
            <Icon icon={external} />
          </>
        ) : (
          <>
            <Icon icon={download} />
            <FormattedMessage {...messages.downloadButton} />
          </>
        )}
      </Button>
    </view.div>
  );
};
